/**
 * Fan Laws (Affinity Laws) - Classical Turbomachinery Scaling Laws
 * 
 * These functions implement the three fundamental fan laws:
 * 1. Volume Flow (Q) ∝ Speed (N)
 * 2. Pressure (P) ∝ Speed² (N²)
 * 3. Power (W) ∝ Speed³ (N³)
 * 
 * Reference: docs/analysis/FAN_LAWS_REFERENCE.md
 */

/**
 * Law #1: Scale volume flow with speed
 * Q₂ = Q₁ × (N₂ / N₁)
 * 
 * @param {number} baseQ2 - Base volume flow ratio (dimensionless)
 * @param {number} referenceRPM - Reference RPM at which baseQ2 was measured
 * @param {number} targetRPM - Target RPM for scaling
 * @returns {number} Scaled volume flow ratio
 */
export function scaleVolumeFlow(baseQ2, referenceRPM, targetRPM) {
  if (!referenceRPM || referenceRPM === 0) {
    console.warn('scaleVolumeFlow: Invalid reference RPM', referenceRPM);
    return baseQ2;
  }
  return baseQ2 * (targetRPM / referenceRPM);
}

/**
 * Law #2: Scale pressure with speed squared
 * P₂ = P₁ × (N₂ / N₁)²
 * 
 * @param {number} baseP2 - Base pressure value (Pa)
 * @param {number} referenceRPM - Reference RPM at which baseP2 was measured
 * @param {number} targetRPM - Target RPM for scaling
 * @returns {number} Scaled pressure (Pa)
 */
export function scalePressure(baseP2, referenceRPM, targetRPM) {
  if (!referenceRPM || referenceRPM === 0) {
    console.warn('scalePressure: Invalid reference RPM', referenceRPM);
    return baseP2;
  }
  const speedRatio = targetRPM / referenceRPM;
  return baseP2 * (speedRatio * speedRatio);
}

/**
 * Law #3: Scale power with speed cubed
 * W₂ = W₁ × (N₂ / N₁)³
 * 
 * @param {number} basePower - Base power consumption (W)
 * @param {number} referenceRPM - Reference RPM at which basePower was measured
 * @param {number} targetRPM - Target RPM for scaling
 * @returns {number} Scaled power consumption (W)
 */
export function scalePower(basePower, referenceRPM, targetRPM) {
  if (!referenceRPM || referenceRPM === 0) {
    console.warn('scalePower: Invalid reference RPM', referenceRPM);
    return basePower;
  }
  const speedRatio = targetRPM / referenceRPM;
  return basePower * (speedRatio * speedRatio * speedRatio);
}

/**
 * Generate a complete fan performance curve for a given RPM
 * using base measurements and fan laws.
 * 
 * @param {Object} baseMeasurements - Base measurement data
 * @param {number} baseMeasurements.reference_rpm - Reference RPM
 * @param {number[]} baseMeasurements.base_q2 - Base Q2 values (volume flow ratios)
 * @param {number[]} baseMeasurements.base_p2 - Base p2 values (pressures in Pa)
 * @param {number} targetRPM - Target RPM for curve generation
 * @returns {Array<{qv: number, psf: number}>} Array of curve points
 */
export function generateFanCurve(baseMeasurements, targetRPM) {
  if (!baseMeasurements) {
    console.warn('generateFanCurve: No base measurements provided');
    return [];
  }

  const { reference_rpm, base_q2, base_p2 } = baseMeasurements;

  if (!reference_rpm || !base_q2 || !base_p2) {
    console.warn('generateFanCurve: Missing required base measurement data', {
      has_reference_rpm: !!reference_rpm,
      has_base_q2: !!base_q2,
      has_base_p2: !!base_p2
    });
    return [];
  }

  if (base_q2.length !== base_p2.length) {
    console.warn('generateFanCurve: Mismatch between base_q2 and base_p2 lengths', {
      q2_length: base_q2.length,
      p2_length: base_p2.length
    });
    return [];
  }

  if (!targetRPM || targetRPM <= 0) {
    console.warn('generateFanCurve: Invalid target RPM', targetRPM);
    return [];
  }

  const curve = [];

  for (let i = 0; i < base_q2.length; i++) {
    // Scale volume flow (Q ∝ N)
    // Note: base_q2 is in m³/s, convert to m³/h by multiplying by 3600
    const q2Scaled = scaleVolumeFlow(base_q2[i], reference_rpm, targetRPM);
    const qv = q2Scaled * 3600; // Convert m³/s to m³/h
    
    // Scale pressure (P ∝ N²)
    const psf = scalePressure(base_p2[i], reference_rpm, targetRPM);
    
    curve.push({ qv, psf });
  }

  return curve;
}

/**
 * Generate fan curves for multiple RPM values
 * 
 * @param {Object} baseMeasurements - Base measurement data
 * @param {number[]} rpmValues - Array of target RPM values
 * @returns {Object} Dictionary mapping RPM to curve points
 */
export function generateFanCurvesForRPMs(baseMeasurements, rpmValues) {
  const curves = {};

  for (const rpm of rpmValues) {
    curves[rpm] = generateFanCurve(baseMeasurements, rpm);
  }

  return curves;
}

/**
 * Generate fan curve for a voltage level using voltage-RPM mappings
 * 
 * @param {Object} baseMeasurements - Base measurement data
 * @param {Object} voltageRpmMappings - Dictionary mapping voltage keys to RPM values
 * @param {string} voltageKey - Voltage key (e.g., "2.2V", "1.5V")
 * @returns {Array<{qv: number, psf: number}>} Array of curve points, or empty array if voltage not found
 */
export function generateCurveForVoltage(baseMeasurements, voltageRpmMappings, voltageKey) {
  if (!voltageRpmMappings || !voltageKey) {
    console.warn('generateCurveForVoltage: Missing voltage mappings or key');
    return [];
  }

  const targetRPM = voltageRpmMappings[voltageKey];

  if (!targetRPM) {
    console.warn(`generateCurveForVoltage: No RPM mapping found for voltage ${voltageKey}`);
    return [];
  }

  return generateFanCurve(baseMeasurements, targetRPM);
}

/**
 * Get RPM value for a voltage key
 * 
 * @param {Object} voltageRpmMappings - Dictionary mapping voltage keys to RPM values
 * @param {string} voltageKey - Voltage key (e.g., "2.2V", "1.5V")
 * @returns {number|null} RPM value or null if not found
 */
export function getRPMForVoltage(voltageRpmMappings, voltageKey) {
  if (!voltageRpmMappings || !voltageKey) {
    return null;
  }
  return voltageRpmMappings[voltageKey] || null;
}

